import { apiSlice } from '../../../store/api/apiSlice';

export interface MemberListItem {
  id: string;
  email: string;
  name: string;
  session_count: number;
  artifact_count: number;
  first_login_at?: string;
  last_active_at?: string;
}

export interface AdminMemberDetail {
  id: string;
  email: string;
  name: string;
  first_login_at?: string;
  last_active_at?: string;
}

export interface AdminMemberArtifactItem {
  id: string;
  title: string;
  profile_slug: string;
  created_at: string;
  updated_at: string;
}

export interface AdminMemberCurrentCoach {
  person_id: string;
  name: string;
  email: string;
}

export interface GetAdminMemberResponse {
  member: AdminMemberDetail;
  session_stats: AdminSessionStats;
  artifacts: AdminMemberArtifactItem[];
  /** Present only when the backend can create synthetic test sessions (MEN-4943). */
  current_coaches?: AdminMemberCurrentCoach[];
}

export interface AdminSessionStats {
  session_count: number;
  last_session_at?: string;
}

/** MEN-5030: the public session-type values Moments stores (MEN-5020). */
export type TestSessionType = 'coaching_session' | 'chem_call';
export type TestSessionFixtureKind = 'completed' | 'scheduled';

export interface CreateTestSessionRequest {
  memberId: string;
  coach_person_id: string;
  session_type: TestSessionType;
  fixture_kind: TestSessionFixtureKind;
  /** RFC 3339 timestamp with offset, exact minute. */
  started_at: string;
  transcript?: string;
  acknowledge_real_history: true;
}

export interface CreateTestSessionResponse {
  fixture_id: string;
  fixture_kind: TestSessionFixtureKind;
  legacy_session_id: string;
  session_id: string;
  session_type: TestSessionType;
  created: boolean;
  session_status: string;
  readiness?: { status: string; reason: string };
  generation?: {
    jobs_enqueued: number;
    already_queued: number;
    already_satisfied: number;
    blocked_targets: number;
    skipped_disabled: number;
  };
}

export type CreateTestSessionErrorCode =
  | 'invalid_request'
  | 'real_history_acknowledgement_required'
  | 'unusable_transcript'
  | 'current_coach_not_found'
  | 'member_not_internal'
  | 'transcript_conflict'
  | 'fixture_conflict'
  | 'projection_unavailable'
  | 'projection_failed'
  | 'fixture_incomplete';

export type MemberSortBy =
  | 'first_login'
  | 'last_active'
  | 'sessions'
  | 'artifacts'
  | 'name'
  | 'email';

export type SortDir = 'asc' | 'desc';

export interface ListMembersResponse {
  members: MemberListItem[];
  total: number;
  page: number;
  per: number;
  sort_by: MemberSortBy;
  sort_dir: SortDir;
}

export interface ListMembersQueryParams {
  q?: string;
  page?: number;
  per?: number;
  sort_by?: MemberSortBy;
  sort_dir?: SortDir;
}

export const membersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listMembers: builder.query<ListMembersResponse, ListMembersQueryParams | undefined>({
      query: (params) => ({
        url: '/admin/members',
        params,
      }),
      providesTags: ['Members'],
    }),

    getAdminMember: builder.query<GetAdminMemberResponse, { memberId: string }>({
      query: ({ memberId }) => `/admin/members/${memberId}`,
      providesTags: ['Members'],
    }),

    createTestSession: builder.mutation<CreateTestSessionResponse, CreateTestSessionRequest>({
      query: ({ memberId, ...body }) => ({
        url: `/admin/members/${memberId}/test-sessions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members'],
    }),
  }),
});

export const { useCreateTestSessionMutation, useGetAdminMemberQuery, useListMembersQuery } =
  membersApi;
